import { useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { ROUTES } from '../helpers/routes-helper'
import { errorLog, infoLog } from '../utils/log-helper'
import { useAppDispatch, useAppSelector } from '../store'
import { uploadMenuScanThunk, selectMenuScanUpload } from '../store/menuScanSlice'
import { logEvent, timeStart, timeEnd } from '../analytics/analytics'
import { persistAnonScan } from '../services/rewardsService'

/**
 * Shared menu scan flow: upload file -> loading page while request is in-flight -> results.
 * Use from Home, Restaurant (no menu), and BottomAppBar scan CTAs.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useMenuScanFlow() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { isAuthenticated } = useAuth0()
  const { uploading } = useAppSelector(selectMenuScanUpload)
  const submittingRef = useRef(false)

  const startScan = useCallback(
    async (
      file: File,
      restaurantId?: string | null,
      uploadMethod?: 'camera' | 'file_picker' | 'native_prompt' | 'unknown',
    ): Promise<void> => {
      if (submittingRef.current || uploading) {
        infoLog('[useMenuScanFlow] Ignoring duplicate submission')
        return
      }
      submittingRef.current = true
      try {
        logEvent('menu_uploaded', {
          upload_method: uploadMethod ?? 'unknown',
          file_type: file.type || undefined,
          screen_name: 'menu_scan',
        })

        // Show loading UI immediately while upload/extraction is running.
        navigate(ROUTES.MENU_SCAN_LOADING, { state: { uploading: true }, replace: true })
        infoLog('[useMenuScanFlow] Uploading file', { name: file.name, size: file.size })

        logEvent('menu_scan_submitted', { screen_name: 'menu_scan_loading' })
        timeStart('menu_scan_duration')

        const result = await dispatch(
          uploadMenuScanThunk({ file, restaurantId: restaurantId ?? undefined })
        ).unwrap()
        const durationMs = timeEnd('menu_scan_duration')

        submittingRef.current = false
        // Anon-only: durably persist the scan so the provisional points number grows and the scan
        // credits on signup claim (in addition to the scan UX). Fire-and-forget, deduped per
        // restaurant on the backend. Keyed by the resolved restaurant (falls back to the input id).
        if (!isAuthenticated && result?.scanId) {
          void persistAnonScan(result?.restaurantId ?? restaurantId)
        }
        const dishCount = result?.dishes?.length ?? 0
        if (result?.scanId && dishCount > 0) {
          logEvent('menu_scan_success', {
            result_count: dishCount,
            has_restaurant_context: !!(result.restaurantId),
            screen_name: 'menu_scan_loading',
          })
          navigate(ROUTES.MENU_SCAN_RESULTS, {
            state: { scanId: result.scanId, result, durationMs },
            replace: true,
          })
        } else if (result?.scanId) {
          // Backend returned a scanId but OCR extracted no dishes — this is NOT a successful
          // scan from the user's perspective. Log it as a failure with a distinct reason so the
          // funnel isn't polluted with empty "successes".
          errorLog('[useMenuScanFlow] Scan returned 0 dishes', result)
          logEvent('menu_scan_failed', { error_type: 'no_dishes', screen_name: 'menu_scan_loading' })
          navigate(ROUTES.MENU_SCAN_RESULTS, {
            state: { scanId: result.scanId, result, durationMs },
            replace: true,
          })
        } else {
          errorLog('[useMenuScanFlow] No scanId in response', result)
          logEvent('menu_scan_failed', { error_type: 'no_scan_id', screen_name: 'menu_scan_loading' })
          navigate(ROUTES.HOME, { replace: true })
        }
      } catch (e) {
        submittingRef.current = false
        errorLog('[useMenuScanFlow] Upload failed', e)
        logEvent('menu_scan_failed', {
          error_type: e instanceof Error ? e.name : 'unknown',
          screen_name: 'menu_scan_loading',
        })
        // Do NOT force-navigate away: the rejected thunk sets upload.error, and MenuScanLoading
        // owns the error+retry screen (reads uploadError). Bouncing to Home here made that screen
        // unreachable and turned every failure (incl. the 60s timeout) into a silent dead-end.
        throw e
      }
    },
    [navigate, uploading, dispatch, isAuthenticated]
  )

  return { startScan, uploading }
}
